import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './my-advisor.scss';

const MyAdvisorDetail = () => {
    const navigate = useNavigate();

    // Existing images from portfolio.json
    const galleryImages = [
        '/portfolio/my-advisor/Simulator Screenshot - iPhone 16 Pro Max - 2025-05-11 at 11.46.03.webp',
        '/portfolio/my-advisor/Simulator Screenshot - iPhone 17 Pro Max - 2026-01-30 at 13.27.56.webp',
        '/portfolio/my-advisor/Simulator Screenshot - iPhone 17 Pro Max - 2026-01-31 at 10.23.17.webp'
    ];

    const heroImage = '/portfolio/my-advisor/my-advisor-appstore-screenshot-1.webp';

    return (
        <div className="container my-advisor-page">
            <button className="back-btn" onClick={() => navigate('/portfolio')}>
                ← Back to Portfolio
            </button>

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-content">
                    <h1>My-Advisor</h1>
                    <p className="tagline">Private AI Chat App for iOS</p>
                    <p className="subtitle">Built for privacy-conscious users who want AI assistance without sending data to servers</p>

                    <div className="badges">
                        {/* <span className="badge live">Live on App Store</span> */}
                        <span className="badge timeline">Built in 5 weeks</span>
                        <span className="badge tech">SwiftUI + Core ML</span>
                    </div>
                </div>
                <div className="hero-image">
                    <img src={heroImage} alt="My-Advisor chat interface" />
                </div>
            </section>

            {/* The Challenge */}
            <section className="challenge-section">
                <h2>The Challenge</h2>
                <p className="intro">
                    Most AI chat apps send every conversation to the cloud. For users handling sensitive information—personal thoughts, work discussions, private questions—that's a dealbreaker.
                </p>
                <p className="ask">
                    <strong>The ask:</strong> Build an AI chat app where everything stays on the user's iPhone. No servers, no data collection, complete privacy.
                </p>
                <div className="requirements">
                    <h3>Key Requirements</h3>
                    <ul>
                        <li>Zero network calls for AI inference</li>
                        <li>Fast responses (no network lag)</li>
                        <li>Familiar chat interface</li>
                        <li>Voice and text input</li>
                        <li>Works completely offline</li>
                    </ul>
                </div>
            </section>

            {/* The Solution */}
            <section className="solution-section">
                <h2>The Solution</h2>
                <p className="intro">
                    Built a native iOS app with a fully on-device AI model using Core ML. Users can chat via text or voice, and nothing ever leaves their phone.
                </p>

                <div className="features-grid">
                    <h3>Key Features</h3>
                    <div className="grid">
                        <div className="feature-card">
                            <h4>On-device AI processing</h4>
                            <p>Core ML model runs locally, zero server calls for conversations</p>
                        </div>
                        <div className="feature-card">
                            <h4>Voice + text input</h4>
                            <p>Speech framework integration for natural conversation flow</p>
                        </div>
                        <div className="feature-card">
                            <h4>Clean chat interface</h4>
                            <p>Familiar messaging UI that feels native and responsive</p>
                        </div>
                        <div className="feature-card">
                            <h4>Complete privacy</h4>
                            <p>All conversations stored locally with iOS Data Protection encryption</p>
                        </div>
                        <div className="feature-card">
                            <h4>Dark mode optimized</h4>
                            <p>Polished UI that feels premium, especially in low-light</p>
                        </div>
                    </div>
                </div>

                {/* Screenshot Gallery - Mid Section */}
                <div className="mid-gallery">
                    {galleryImages.slice(0, 2).map((img, idx) => (
                        <div key={idx} className="gallery-item">
                            <img src={img} alt={`My-Advisor screenshot ${idx + 1}`} />
                        </div>
                    ))}
                </div>

                <div className="technical-approach">
                    <h3>Technical Approach</h3>
                    <div className="grid">
                        <div className="tech-card">
                            <h4>SwiftUI for UI layer</h4>
                            <p>Reactive, declarative interface that adapts to content</p>
                        </div>
                        <div className="tech-card">
                            <h4>Core ML for inference</h4>
                            <p>On-device language model (3B parameters, quantized to 2GB)</p>
                        </div>
                        <div className="tech-card">
                            <h4>Speech framework</h4>
                            <p>On-device speech-to-text transcription for voice input</p>
                        </div>
                        <div className="tech-card">
                            <h4>Local storage</h4>
                            <p>CoreData with encryption, conversations never uploaded</p>
                        </div>
                        <div className="tech-card">
                            <h4>Performance optimization</h4>
                            <p>Neural Engine acceleration, ~500ms inference time on iPhone 13+</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Result */}
            <section className="result-section">
                <h2>The Result</h2>
                <p className="intro">
                    Shipped a production AI chat app that delivers intelligent responses without compromising user privacy. Users can think out loud, draft sensitive content, and get AI assistance—all without data leaving their device.
                </p>

                <div className="outcomes">
                    <h3>Key Outcomes</h3>
                    <ul>
                        <li><span className="check">✓</span> Zero-latency responses – No network calls, instant feedback</li>
                        <li><span className="check">✓</span> Works completely offline – No internet required after initial app download</li>
                        <li><span className="check">✓</span> Full user privacy maintained – No data collection, no tracking, no servers</li>
                        <li><span className="check">✓</span> Native iOS experience – Not a web wrapper, true native app performance</li>
                    </ul>
                </div>

                {/* Remaining Screenshot */}
                <div className="result-image">
                    <img src={galleryImages[2]} alt="My-Advisor in action" />
                </div>
            </section>

            {/* What This Proves */}
            <section className="proves-section">
                <h2>What This Proves</h2>
                <p className="headline">We can build AI/ML apps for iOS.</p>
                <p>If your startup needs:</p>
                <ul>
                    <li>On-device machine learning (Core ML, Create ML)</li>
                    <li>Privacy-focused AI features</li>
                    <li>Speech/NLP integration</li>
                    <li>Real-time inference without servers</li>
                    <li>Apps that work offline</li>
                </ul>
                <p className="emphasis">...we've done it in production.</p>

                <div className="why-matters">
                    <h3>Why This Matters</h3>
                    <p>For health, finance, legal, or therapy apps—anywhere privacy is critical—on-device AI is the only acceptable solution. My-Advisor proves it's not just possible, but practical.</p>
                </div>
            </section>

            {/* Tech Stack */}
            <section className="tech-stack-section">
                <h2>Tech Stack</h2>
                <div className="stack-table">
                    <div className="stack-row">
                        <span className="label">Language</span>
                        <span className="value">Swift</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">UI Framework</span>
                        <span className="value">SwiftUI</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">Machine Learning</span>
                        <span className="value">Core ML (on-device language model, 3B parameters quantized)</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">Speech</span>
                        <span className="value">Speech framework for voice-to-text (on-device recognition)</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">Storage</span>
                        <span className="value">Local persistence with iOS Data Protection encryption</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">Architecture</span>
                        <span className="value">MVVM pattern, clean separation of concerns</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">Minimum Requirements</span>
                        <span className="value">iOS 17+, iPhone 12 or newer, 4GB free storage</span>
                    </div>
                </div>
            </section>

            {/* Design Highlights */}
            <section className="design-section">
                <h2>Design Highlights</h2>

                <div className="design-grid">
                    <div className="design-card">
                        <h3>Privacy-First UX</h3>
                        <p>Every interaction reinforces privacy:</p>
                        <ul>
                            <li>First launch: "Your conversations never leave this device"</li>
                            <li>No account creation (privacy by default)</li>
                            <li>No sync, no cloud backup prompts</li>
                            <li>Clear messaging about on-device processing</li>
                        </ul>
                    </div>

                    <div className="design-card">
                        <h3>Performance Optimization</h3>
                        <ul>
                            <li>Model pre-loads in background on app launch</li>
                            <li>First inference cached for instant follow-ups</li>
                            <li>Sliding context window (efficient memory usage)</li>
                            <li>Haptic feedback confirms every interaction</li>
                        </ul>
                    </div>

                    <div className="design-card">
                        <h3>Accessibility</h3>
                        <ul>
                            <li>Full VoiceOver support</li>
                            <li>Dynamic Type (text scales with system settings)</li>
                            <li>High contrast mode support</li>
                            <li>Keyboard shortcuts for power users</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Project Timeline */}
            <section className="timeline-section">
                <h2>Project Scope & Timeline</h2>
                <div className="timeline-info">
                    <p><strong>Duration:</strong> 5 weeks from kickoff to App Store submission</p>
                    <p><strong>Team:</strong> Solo developer (me)</p>
                </div>

                <div className="deliverables">
                    <h3>Deliverables</h3>
                    <ul>
                        <li>Native iOS app (Swift/SwiftUI)</li>
                        <li>On-device Core ML integration</li>
                        <li>Voice + text input</li>
                        <li>Encrypted local storage</li>
                        <li>App Store submission</li>
                        <li>Documentation</li>
                    </ul>
                </div>

                <div className="week-breakdown">
                    <div className="week">
                        <span className="week-label">Week 1</span>
                        <span className="week-task">Model selection, testing, and optimization</span>
                    </div>
                    <div className="week">
                        <span className="week-label">Week 2</span>
                        <span className="week-task">Core ML conversion and integration</span>
                    </div>
                    <div className="week">
                        <span className="week-label">Week 3-4</span>
                        <span className="week-task">UI development and chat flow</span>
                    </div>
                    <div className="week">
                        <span className="week-label">Week 5</span>
                        <span className="week-task">Polish, testing, and App Store prep</span>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <h2>Want Something Similar?</h2>
                <p>If you're building an AI-powered iOS app and need:</p>
                <ul>
                    <li>On-device ML integration for privacy</li>
                    <li>Core ML model optimization and deployment</li>
                    <li>Privacy-first architecture (zero data collection)</li>
                    <li>Voice/speech features</li>
                    <li>Clean, polished native UI</li>
                </ul>
                <p className="pricing-note">We can help. Fixed-price packages starting at $15k, 4–8 week timelines.</p>

                <div className="relevant-for">
                    <h3>Relevant For:</h3>
                    <div className="use-cases">
                        <div className="use-case">
                            <h4>Health & Medical Apps</h4>
                            <p>Mental health journaling, symptom checkers, therapy assistants</p>
                        </div>
                        <div className="use-case">
                            <h4>Finance Apps</h4>
                            <p>Personal finance advice, budget planning, investment research</p>
                        </div>
                        <div className="use-case">
                            <h4>Legal Apps</h4>
                            <p>Contract analysis, legal Q&A, document review</p>
                        </div>
                        <div className="use-case">
                            <h4>Enterprise Apps</h4>
                            <p>Internal AI assistants where data can't leave the device</p>
                        </div>
                    </div>
                </div>

                <div className="cta-buttons">
                    <Link to="/pricing" className="flat-button primary">
                        View iOS Packages & Pricing →
                    </Link>
                    <a
                        href="https://calendly.com/vermresearch-proton/30min"
                        target="_blank"
                        rel="noreferrer"
                        className="flat-button secondary"
                    >
                        Schedule Free 20-Min Call
                    </a>
                </div>
            </section>

            {/* Related Work */}
            <section className="related-section">
                <h3>Related Work</h3>
                <p>Also check out: <Link to="/portfolio/readdy-app">Readdy</Link> – 4.9-star habit tracking app with gamification</p>
            </section>

            {/* Footer Meta */}
            <section className="meta-section">
                <div className="meta-tags">
                    <span><strong>Project Type:</strong> iOS App Development, AI/ML Integration</span>
                    <span><strong>Industry:</strong> Privacy Tech, AI Utilities</span>
                    <span><strong>Timeline:</strong> 5 weeks</span>
                    <span><strong>Technologies:</strong> Swift, SwiftUI, Core ML, Speech Framework</span>
                    <span><strong>Platform:</strong> iOS 17+</span>
                </div>
            </section>
        </div>
    );
};

export default MyAdvisorDetail;
